/**
 * Two header icon buttons: language switcher + profile avatar.
 * Used in every top-level screen header.
 */
import { useEffect, useRef, useState } from 'react';
import { LOCALE_LABELS, SUPPORTED_LOCALES, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useRouter } from '../lib/router';
import { cn } from '../lib/cn';

export function HeaderActions(): JSX.Element {
  const { me } = useSession();
  const { navigate, path } = useRouter();
  const onProfile = path.startsWith('/profile');

  return (
    <div className="flex items-center gap-2">
      <LanguageButton />
      <button
        type="button"
        onClick={() => navigate('/profile')}
        aria-label="Profile"
        className={cn(
          'flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 transition',
          onProfile
            ? 'border-brand-green'
            : 'border-[var(--border-light)] hover:border-brand-green/50',
        )}
      >
        {me?.avatarUrl ? (
          <img src={me.avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="bg-brand-navy text-base font-semibold text-brand-cream flex h-full w-full items-center justify-center">
            {(me?.displayName ?? '?').slice(0, 1).toUpperCase()}
          </span>
        )}
      </button>
    </div>
  );
}

function LanguageButton(): JSX.Element {
  const { locale, setLocale } = useT();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!popRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border-light)] bg-tg-bg-secondary text-sm font-semibold text-tg-text transition hover:border-brand-green/50"
      >
        <GlobeIcon />
        <span className="sr-only">{LOCALE_LABELS[locale]}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-12 z-50 w-40 overflow-hidden rounded-card border border-[var(--border-light)] bg-tg-bg shadow-md"
        >
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              role="menuitem"
              onClick={() => {
                void setLocale(l);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition',
                locale === l
                  ? 'bg-[var(--border-medium)] font-semibold text-tg-text'
                  : 'text-tg-text hover:bg-tg-bg-secondary',
              )}
            >
              <span>{LOCALE_LABELS[l]}</span>
              {locale === l && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GlobeIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
